import { strFromU8, unzipSync } from "fflate";
import { MAX_PERSHING_ZIP_BYTES } from "./iscaArchive";

export const PERSHING_DAILY_FILE_TYPES = ["GCUS", "ISCA", "GTDE", "GSDE"];
export const REQUIRED_PERSHING_DAILY_FILE_TYPES = ["GCUS", "ISCA", "GTDE"];

const FILE_NAME_RE = /^(\d{2})(\d{2})(\d{2})\.(GCUS|ISCA|GTDE|GSDE)\.ZIP$/i;

export function parsePershingDailyFileName(fileName) {
  const match = String(fileName || "").trim().match(FILE_NAME_RE);
  if (!match) return null;
  const [, mm, dd, yy, rawType] = match;
  const year = 2000 + Number(yy);
  const month = Number(mm);
  const day = Number(dd);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    return null;
  }
  return { type: rawType.toUpperCase(), date: `${year}-${mm}-${dd}`, stem: `${mm}${dd}${yy}` };
}

export function classifyPershingDailyFiles(files) {
  const classified = {};
  let packageDate = null;
  for (const file of files || []) {
    const identity = parsePershingDailyFileName(file?.name);
    if (!identity) {
      throw new Error(`${file?.name || "Selected file"} is not named MMDDYY.GCUS|ISCA|GTDE|GSDE.ZIP.`);
    }
    if (classified[identity.type]) {
      throw new Error(`More than one ${identity.type} ZIP was selected.`);
    }
    if (packageDate && packageDate !== identity.date) {
      throw new Error(`All selected Pershing files must have the same filename date (${packageDate} and ${identity.date} were selected).`);
    }
    packageDate = identity.date;
    classified[identity.type] = { file, ...identity };
  }
  return { date: packageDate, files: classified };
}

export function extractPershingDailyTextZip(bytes, expectedType, expectedStem) {
  const archiveBytes = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  if (!archiveBytes.length) throw new Error(`The selected ${expectedType} ZIP file is empty.`);
  if (archiveBytes.length > MAX_PERSHING_ZIP_BYTES) {
    throw new Error(`The selected ${expectedType} ZIP exceeds the 25 MB upload limit.`);
  }
  let archive;
  try {
    archive = unzipSync(archiveBytes);
  } catch (error) {
    throw new Error(`Unable to open the Pershing ${expectedType} ZIP file: ${error.message}`);
  }
  const expectedName = `${expectedStem}.${expectedType}`;
  const members = Object.entries(archive).filter(([name]) => !name.endsWith("/"));
  if (members.length !== 1) {
    throw new Error(`The ${expectedType} ZIP must contain exactly one data file.`);
  }
  const [memberName, memberBytes] = members[0];
  const baseName = memberName.replace(/\\/g, "/").split("/").pop();
  if (baseName?.toUpperCase() !== expectedName) {
    throw new Error(`The ${expectedType} ZIP contains ${baseName}; expected ${expectedName}.`);
  }
  const contents = strFromU8(memberBytes);
  const transactionCode = expectedType === "GTDE" ? "GE" : "GS";
  const records = contents.split(/\r?\n/).filter((line) => line.length > 0);
  const hasEnvelope = records[0]?.startsWith("BOF") && records.at(-1)?.startsWith("EOF");
  const isHeaderOnly = records.length === 2;
  const hasTransactionRecords = records.some((line) => line.startsWith(`${transactionCode}A`))
    && records.some((line) => line.startsWith(`${transactionCode}B`));
  if (!hasEnvelope || (!isHeaderOnly && !hasTransactionRecords)) {
    throw new Error(`${expectedName} does not contain the expected Pershing ${expectedType} records.`);
  }
  return { memberName, memberBytes, contents };
}
